import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SweetAlert from 'react-bootstrap-sweetalert';
import { isEmpty } from 'lodash';
import { Col, Row } from 'reactstrap';
import uuid from 'react-uuid';
import { useDispatch, useSelector } from 'react-redux';
import { BidStatus } from 'stock-vault-admin-panel/src/constants/BidStatus';
import {
  VehicleServiceHistory,
  VehicleServiceHistoryName,
} from 'stock-vault-admin-panel/src/constants/VehicleServiceHistory';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DamageTypesName } from 'stock-vault-admin-panel/src/constants/DamageTypes';
import classNames from 'classnames';
import { AuctionStatusType } from '../../../constants/AuctionStatusType';
import {
  AuctionBarColors,
  AuctionStatus,
  AuctionStatusFromSse,
  AuctionStatusName,
} from '../../../constants/AuctionStatus';
import RouterPaths from '../../../constants/RouterPaths';
import AuctionDetailsSellerSection from '../AuctionDetailsSellerSection';
import { auctionListType } from '../../../constants/AuctionListType';
import AuctionDetailsBuyerSection from '../AuctionDetailsBuyerSection';
import magnifierIcon from '../../../assets/img/icons/zoom_red.svg';
import { UploadFileTypes } from '../../../constants/UploadFileTypes';
import { dateToString } from '../../utils/date/Date.utils';
import { preparePriceFormat } from '../../helpers/preparePriceFormat';
import {
  AUCTION_CANCELLED,
  AUCTION_UPDATED,
  BID_PLACED,
  SSE_BUYER,
  SSE_SELLER,
} from '../../../api/SSEClient';
import { CommonString } from '../../../constants/CommonString';
import { capitalizeString } from '../../helpers/capitalizeString';
import { setRelaunchAllow } from '../../../myvehicles/MyVehiclesActions';
import { BasicCarDataFieldsName } from '../../../constants/BasicCarDataFields';
import { pencesToPounds, poundsToPences } from '../../helpers/pencesToPounds';
import AuctionMainDescriptionContainer, { SlotItems } from './AuctionMainDescriptionContainer';
import { AccountLayoutTitle, AccountLayoutWrapper } from '../AccountLayoutWrapper';
import PlaceBidBox from './PlaceBidBox';
import { prepareBidsData } from '../../helpers/prepareBidsData';
import { getBidItemMessage } from '../../helpers/auctionItemHelper';
import BoxStatusVariants from '../../../constants/BoxStatusVariants';
import AuctionStatusBox from '../../../myvehicles/AuctionStatusBox';
import { scrollToElement } from '../../helpers/generalHelper';
import locationIcon from '../../../assets/img/icons/location_on.svg';
import ImagesSlider from './ImagesSlider';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import useDefaultBidLocationId from '../../hooks/useDefaultBidLocationId';
import { useIsAllLocationsSelected } from '../../hooks/useLocationIdCookie';
import shareIcon from '../../../assets/img/icons/share.svg';
import closeIcon from '../../../assets/img/icons/close.svg';
import favoriteIcon from '../../../assets/img/icons/favorite.svg';
import favoriteRedIcon from '../../../assets/img/icons/favorite-red.svg';
import arrowIcon from '../../../assets/img/icons/arrow_back.svg';

import {
  addFavouriteAuction,
  deleteFavouriteAuction,
  getFavouriteAuctions,
} from '../../../settings/SettingsActions';
import VehicleConditionMap from '../../../addvehicle/condition/VehicleConditionMap';
import cameraIcon from '../../../assets/img/icons/camera.svg';
import { AuctionListTemplateContextType } from '../new/AuctionListTemplate';
import { mapNumberOfKeys } from '../../helpers/numberOfKeys';
import { ServiceTypeName } from '../../../constants/ServiceHistory';
import ShareAuctionBox from '../ShareAuctionBox';

const DERIVATIVE_LIMIT = 15;

const {
  REGISTRATION_PLATE,
  MILEAGE,
  YEAR,
  MOT,
  CAR_TYPE,
  TRANSMISSION,
  ENGINE_SIZE,
  FUEL,
  COLOUR,
  DATE_OFF_FIRST_REGISTRATION,
  PREVIOUS_KEEPERS_IN_TOTAL,
  VIN,
  LOGBOOK_STATUS,
  ULEZ,
  NUMBER_OF_KEYS,
  VAT_QUALIFYING,
  VEHICLE_TYPE,
} = BasicCarDataFieldsName;

function AuctionDetailsItem({
  item,
  listType,
  refreshItem,
  sseClient,
  typeSseClient,
  nextStep,
  setIsRepublish,
  inWrapper,
  wrapperProps,
  auctionTimestamp,
  context,
}) {
  const organizationId = useSelector((state) => state.auth.get('organizationId'));
  const currentMemberId = useSelector((state) => state.auth.get('id'));

  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [shouldPriceBeGreen, setShouldPriceBeGreen] = useState(false);
  const [highestBid, setHighestBid] = useState(null);
  const [proxyBid, setProxyBid] = useState(null);
  const [bidNotification, setBidNotification] = useState();
  const [bidsNotification, setBidsNotification] = useState();
  const [status, setStatus] = useState(false);
  const [isAuctionRelaunched, setIsAuctionRelaunched] = useState(false);
  const [windowFocus, setWindowFocus] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isDesktop = useMediaQuery('(min-width: 1425px)');
  const navigate = useNavigate();
  const params = useParams();

  const bidsData = useMemo(
    () => (bidsNotification && bidsNotification.length > 0 ? bidsNotification : item?.bids),
    [bidsNotification, item],
  );

  const {
    bidMyOrganizationIsHighest,
    highestBidMyOrganization,
    bidsMyOrganization,
    winningBid,
  } = useMemo(() => prepareBidsData(bidsData, organizationId), [bidsData, organizationId]);

  const windowOnFocus = () => {
    setWindowFocus(true);
  };

  const windowOnBlur = () => {
    setWindowFocus(false);
  };

  const onNavItemClick = (e, id) => {
    e.preventDefault();
    scrollToElement(id, -140);
  };

  useEffect(() => {
    window.addEventListener('focus', windowOnFocus);
    window.addEventListener('blur', windowOnBlur);
    return () => {
      window.removeEventListener('focus', windowOnFocus);
      window.removeEventListener('blur', windowOnBlur);
    };
  }, []);

  useEffect(() => {
    setHighestBid(pencesToPounds(item?.highestBidInPence));
    setShouldPriceBeGreen(shouldPriceBeGreen || item?.shouldPriceBeGreen);
    sseClient.listen([item.id], (response) => {
      setStatus(!!AuctionStatusFromSse[response.type]);
      if (response.type === BID_PLACED || response.type === AUCTION_UPDATED) {
        handleBidEvent(response);
      }
      if (response?.type === AUCTION_CANCELLED) {
        dispatch(setRelaunchAllow(true));
      }
      refreshItem();
    });
    return () => {
      sseClient.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const changeStatus = (data) => {
    setStatus(data);
  };

  const isPaymentProgress = useCallback(
    () =>
      !item?.seller?.firstName &&
      !item?.location?.address &&
      !item?.seller?.organizationContact?.tradeName,
    [item],
  );

  const handleBidEvent = (event) => {
    setIsAuctionRelaunched(false);
    setShouldPriceBeGreen(shouldPriceBeGreen || event.shouldPriceBeGreen);
    setHighestBid(pencesToPounds(event.highestBidInPence));
    setProxyBid(pencesToPounds(event.proxyBidInPence));
    if (typeSseClient === SSE_SELLER) {
      if (!event.highestBids.length) {
        return false;
      }
      const highestAmountBid = event.highestBids.reduce((acc, current) => {
        if (current.amountInPence > acc.amountInPence) {
          return current;
        }
        return acc;
      });
      if (highestAmountBid) {
        const bidAmount = preparePriceFormat(highestAmountBid.amountInPence, true);
        setBidNotification(bidAmount);
      }
    }
    if (typeSseClient === SSE_BUYER) {
      setBidsNotification(event.highestBids);
    }
    return true;
  };

  useEffect(() => {
    if (!isEmpty(item?.vehicle?.vehicleMedia?.photos)) {
      const preparedPhotos = item?.vehicle?.vehicleMedia?.photos.map((item) => {
        if (item?.fileUrl) {
          return {
            id: item?.mediaMetadataId,
            fileUrl: item?.fileUrl,
            thumbnailUrl: item?.thumbnailUrl,
            perspective: item?.perspective,
            damages: item?.damages
              ? item.damages.map((damage) => ({
                  id: damage?.mediaMetadataId,
                  fileUrl: damage?.fileUrl,
                  damageType: damage?.damageType,
                }))
              : [],
          };
        }
        return null;
      });

      setPhotos(preparedPhotos);
    }
    if (!isEmpty(item?.vehicle?.vehicleMedia?.videos)) {
      const preparedVideo = item?.vehicle?.vehicleMedia?.videos.map((item) => {
        if (item.fileUrl) {
          return {
            fileUrl: item.fileUrl,
            id: item?.mediaMetadataId,
            previewId: item?.previewMediaMetadataId,
            previewUrl: item?.previewUrl,
            type: UploadFileTypes.VIDEO_TYPE,
          };
        }
        return null;
      });
      setVideos(preparedVideo);
    }
  }, [item]);

  const serviceHistoryThumbnailUrls =
    item?.vehicle?.serviceHistory?.photoRecords &&
    item?.vehicle?.serviceHistory?.photoRecords.map((item) =>
      item.thumbnailUrl ? item.thumbnailUrl : item.fileUrl,
    );

  const renderLocationValue = () => {
    if (item?.location?.city) {
      if (item?.location?.county) {
        return `${item?.location?.city}, ${item?.location?.county}`;
      }
      return `${item?.location?.city}`;
    }
    return CommonString.NOT_SPECIFIED;
  };

  const carInfo = [
    {
      title: REGISTRATION_PLATE,
      value: item?.vehicle?.registrationPlate || CommonString.NOT_SPECIFIED,
    },
    {
      title: MILEAGE,
      value: item?.vehicle?.mileage
        ? `${item?.vehicle?.mileage} miles`
        : CommonString.NOT_SPECIFIED,
    },
    {
      title: TRANSMISSION,
      value: capitalizeString(item?.vehicle?.transmission) || CommonString.NOT_SPECIFIED,
    },
    {
      title: CAR_TYPE,
      value: item?.vehicle?.carType || CommonString.NOT_SPECIFIED,
    },
    {
      title: YEAR,
      value: item?.vehicle?.year || CommonString.NOT_SPECIFIED,
    },
    ...(item?.vehicle?.engineSize
      ? [
          {
            title: ENGINE_SIZE,
            value: `${item.vehicle.engineSize} cm3`,
          },
        ]
      : []),
    {
      title: LOGBOOK_STATUS,
      value: item?.vehicle?.v5cLogbook || CommonString.NOT_SPECIFIED,
    },
    {
      title: ULEZ,
      value: item?.vehicle?.ulezCompliant || CommonString.NOT_SPECIFIED,
    },
    {
      title: FUEL,
      value: item?.vehicle?.fuel || CommonString.NOT_SPECIFIED,
    },
    {
      title: COLOUR,
      value: capitalizeString(item?.vehicle?.colour) || CommonString.NOT_SPECIFIED,
    },
    {
      title: PREVIOUS_KEEPERS_IN_TOTAL,
      value:
        item?.vehicle?.previousKeepersInTotal !== undefined &&
        item?.vehicle?.previousKeepersInTotal !== null
          ? item?.vehicle?.previousKeepersInTotal
          : CommonString.NOT_SPECIFIED,
    },
    {
      title: VIN,
      value: item?.vehicle?.vin
        ? item?.vehicle?.vin?.toUpperCase()
        : CommonString.NOT_SPECIFIED,
    },
    {
      title: DATE_OFF_FIRST_REGISTRATION,
      value:
        dateToString(item?.vehicle?.dateOfFirstRegistration) || CommonString.NOT_SPECIFIED,
    },
    {
      title: MOT,
      value: dateToString(item?.vehicle?.mot) || CommonString.NOT_SPECIFIED,
    },
    ...(item?.vehicle?.numbersOfKeyIncluded
      ? [
          {
            title: NUMBER_OF_KEYS,
            value: mapNumberOfKeys(item?.vehicle?.numbersOfKeyIncluded),
          },
        ]
      : []),
    ...(item?.vehicle?.vehicleType
      ? [
          {
            title: VEHICLE_TYPE,
            value: capitalizeString(item.vehicle.vehicleType),
          },
        ]
      : []),
    ...(item?.vehicle?.vatQualifying != null
      ? [
          {
            title: VAT_QUALIFYING,
            value: item.vehicle.vatQualifying ? 'YES' : 'NO',
          },
        ]
      : []),
  ];

  const buyerDetails = [
    {
      title: 'Company Name',
      value: item?.buyer?.organizationContact?.tradeName,
    },
    {
      title: 'Contact Name',
      value: `${item?.buyer?.firstName || ''} ${item?.buyer?.lastName || ''}`,
    },
    {
      title: 'Email',
      value: item?.buyer?.email,
    },
    {
      title: 'Mobile',
      value: item?.buyer?.phoneNumber,
    },
    {
      title: 'Landline',
      value: item?.buyer?.landlinePhoneNumber,
    },
    {
      title: 'City',
      value: item?.buyer?.organizationContact?.location?.city,
    },
    {
      title: 'Address - Line 1',
      value: item?.buyer?.organizationContact?.location?.address,
    },
    {
      title: 'Address - Line 2',
      value: item?.buyer?.organizationContact?.location?.address2,
    },
    {
      title: 'County',
      value: item?.buyer?.organizationContact?.location?.county,
    },
    {
      title: 'Postcode',
      value: item?.buyer?.organizationContact?.location?.postalCode?.postcode,
    },
  ];

  const sellerDetails = [
    {
      title: 'Company Name',
      value: item?.seller?.organizationContact?.tradeName,
    },
    {
      title: 'Contact Name',
      value:
        item?.seller.firstName && item?.seller.lastName
          ? `${item?.seller.firstName} ${item?.seller.lastName}`
          : '-',
    },
    {
      title: 'Email',
      value: item?.seller.email,
    },
    {
      title: 'Mobile',
      value: item?.seller?.phoneNumber,
    },
    {
      title: 'Landline',
      value: item?.seller?.organizationContact?.landlinePhoneNumber,
    },
    {
      title: 'City',
      value: item?.location?.city,
    },
    {
      title: 'Address - Line 1',
      value: item?.location?.address,
    },
    {
      title: 'Address - Line 2',
      value: item?.location?.address2,
    },
    {
      title: 'County',
      value: item?.location?.county,
    },
    {
      title: 'Postcode',
      value: item?.location?.postalCode?.postcode,
    },
  ];

  const minimumPrice = pencesToPounds(item?.minimumPriceInPence);

  // eslint-disable-next-line no-unused-vars
  let backLinkUrl;
  // eslint-disable-next-line no-unused-vars
  let backLinkText;

  switch (listType) {
    case auctionListType.MY_VEHICLES:
      backLinkUrl = RouterPaths.MY_VEHICLES;
      backLinkText = 'Back to "Selling"';
      break;
    case auctionListType.MY_BIDS:
      backLinkUrl = RouterPaths.MY_BIDS;
      backLinkText = 'Back to "Buying"';
      break;
    case auctionListType.SEARCH:
    default:
      // eslint-disable-next-line no-unused-vars
      backLinkUrl = RouterPaths.SEARCH;
      // eslint-disable-next-line no-unused-vars
      backLinkText = 'Back to the search results';
      break;
  }

  // eslint-disable-next-line no-unused-vars
  const renderDetailsSection = () => {
    if (
      listType === auctionListType.MY_BIDS ||
      (listType === auctionListType.SEARCH &&
        item?.seller?.organizationContact?.id !== organizationId)
    ) {
      return (
        <AuctionDetailsBuyerSection
          highestBidInPence={poundsToPences(highestBid) || minimumPrice || 0}
          item={item}
          refreshItem={refreshItem}
          bids={bidsNotification}
          changeStatus={changeStatus}
          status={status}
        />
      );
    }
    return (
      <AuctionDetailsSellerSection
        item={item}
        refreshItem={refreshItem}
        bidNotification={bidNotification}
        changeStatus={changeStatus}
        status={status}
        isAuctionRelaunched={isAuctionRelaunched}
        setIsAuctionRelaunched={setIsAuctionRelaunched}
        nextStep={nextStep}
        setIsRepublish={setIsRepublish}
        context={context}
      />
    );
  };

  // const isAuctionLabel = useMemo(() => {
  //   return !!(item?.type || AuctionStatusTypeName[item?.type]);
  // }, [item.type]);

  const getAuctionBidMessage = useCallback(() => {
    const mapBidColorToBoxStatus = (color) => {
      switch (color) {
        case AuctionBarColors.RED:
        case AuctionBarColors.STRONG_RED:
          return BoxStatusVariants.RED;
        case AuctionBarColors.GREEN:
          return BoxStatusVariants.GREEN;
        case AuctionBarColors.BLUE:
          return BoxStatusVariants.PRIMARY;
        default:
          return BoxStatusVariants.PRIMARY;
      }
    };

    if (bidsMyOrganization?.length) {
      const messageData = getBidItemMessage(
        bidMyOrganizationIsHighest,
        highestBidMyOrganization?.amountInPence,
        item,
        winningBid?.bidderId === currentMemberId,
      );

      return messageData
        ? {
            bidBarMessage: messageData.message,
            bidBarColor: mapBidColorToBoxStatus(messageData.statusColor),
          }
        : null;
    }

    return null;
  }, [item, bidMyOrganizationIsHighest, highestBidMyOrganization, bidsMyOrganization]);

  const { bidBarMessage, bidBarColor } = getAuctionBidMessage() || {};

  const canShowAuctionWinInformation = () =>
    listType === auctionListType.MY_BIDS &&
    item?.status === AuctionStatus.SOLD &&
    !isPaymentProgress() &&
    bidMyOrganizationIsHighest;

  const canShowBidsMyOrganisation = () =>
    (listType === auctionListType.MY_BIDS || listType === auctionListType.SEARCH) &&
    bidsMyOrganization?.length;

  const canShowBuyersInformation = () =>
    listType === auctionListType.MY_VEHICLES &&
    item?.buyer?.organizationContact?.tradeName &&
    item.status === AuctionStatus.SOLD;

  // eslint-disable-next-line react/no-unstable-nested-components
  function VehicleDescriptionContent() {
    if (!item?.vehicle?.description && !item?.vehicle?.defaultDescription)
      return CommonString.DESCRIPTION_NOT_SPECIFIED;

    if (item?.vehicle?.defaultDescription) {
      return (
        <span style={{ whiteSpace: 'pre-line' }}>
          {item?.vehicle?.description || ''}
          <br />
          {item.vehicle.defaultDescription}
        </span>
      );
    }

    return <span style={{ whiteSpace: 'pre-line' }}>{item.vehicle.description}</span>;
  }

  const [currentDamageIndex, setCurrentDamageIndex] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(null);

  useEffect(() => {
    if (currentDamageIndex !== null || currentImageIndex !== null) {
      const escCloser = (e) => {
        const key = e.key || e.code;
        if (key === 'Escape' || key === 'Esc' || e.keyCode === 27) {
          e.preventDefault();
          e.stopPropagation();
          if (currentDamageIndex !== null) {
            setCurrentDamageIndex(null);
            setCurrentImageIndex(null);
          } else if (currentImageIndex !== null) {
            setCurrentImageIndex(null);
          }
        }
      };
      window.addEventListener('keydown', escCloser, true);
      return () => window.removeEventListener('keydown', escCloser, true);
    }
    return undefined;
  }, [currentDamageIndex, currentImageIndex]);

  const handleNext = () => {
    setCurrentImageIndex((prevIndex) => prevIndex + 1);
  };

  const handlePrev = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const renderSummary = () => (
    <div>
      <Row
        className="auction-details-section summary__main-description-container d-flex m-0"
        id="vehicle-details-info"
      >
        <Col>
          {canShowAuctionWinInformation() && (
            <>
              <Row className="main-description-container__section-title p-4">
                <Col md={12} className="p-0 pb-0 text-lg">
                  Seller’s Details
                </Col>
              </Row>
              <Row className="summary__feature-list mt-0 p-4" xs={12}>
                {sellerDetails &&
                  sellerDetails.map((item, index) => (
                    <Col
                      key={item.title}
                      className={`p-0 ${index % 2 === 0 ? 'pe-md-4' : 'ps-md-4'}`}
                      xs={12}
                      md={6}
                    >
                      <div className="summary__list-item text-base">
                        <p className="m-0">{item.title}</p>
                        <p className="fw-600 m-0">{item.value}</p>
                      </div>
                    </Col>
                  ))}
              </Row>
            </>
          )}
          {canShowBuyersInformation() && (
            <>
              <Row className="main-description-container__section-title  p-4">
                <Col md={12} className="p-0 pb-0 text-lg">
                  Buyer’s Details
                </Col>
              </Row>
              <Row className="summary__feature-list mt-0 p-4" xs={12}>
                {buyerDetails &&
                  buyerDetails.map((item, index) => (
                    <Col
                      key={item.title}
                      className={`p-0 ${index % 2 === 0 ? 'pe-md-4' : 'ps-md-4'}`}
                      xs={12}
                      md={6}
                    >
                      <div className="summary__list-item text-base">
                        <p className="m-0">{item.title}</p>
                        <p className="fw-600 m-0">{item.value}</p>
                      </div>
                    </Col>
                  ))}
              </Row>
            </>
          )}
          <Row className="main-description-container__section-title p-4" id="overview">
            <Col md={12} className="p-0 pb-0 text-lg">
              Overview
            </Col>
          </Row>
          <Row className="summary__feature-list mt-0 p-4" xs={12}>
            <div className="d-flex flex-column flex-md-row justify-content-between p-0">
              <Col sm={12} md={6} className="pe-md-4">
                {carInfo &&
                  carInfo.slice(0, 8).map((item) => (
                    <Col key={item.title} className="p-0" xs={12}>
                      <div className="summary__list-item text-base">
                        <p className="m-0">{item.title}</p>
                        <p className="fw-600 m-0">{item.value}</p>
                      </div>
                    </Col>
                  ))}
              </Col>
              <Col sm={12} md={6} className="ps-md-4">
                {carInfo &&
                  carInfo.slice(8).map((item) => (
                    <Col key={item.title} className="p-0" xs={12}>
                      <div className="summary__list-item text-base">
                        <p className="m-0">{item.title}</p>
                        <p className="fw-600 m-0">{item.value}</p>
                      </div>
                    </Col>
                  ))}
              </Col>
            </div>
            <div className="summary__feature-list-description" id="description">
              <p className="summary__feature-list-description-title">Description</p>
              <p className="summary__feature-list-description-content">
                <VehicleDescriptionContent />
              </p>
            </div>
          </Row>
        </Col>
      </Row>
      <Row
        className="main-description-container__section-title m-0 mt-0"
        // id="vehicle-details-specification"
        id="equipment"
      >
        <Col md={12} className="p-0  text-lg p-4">
          Equipment
        </Col>
      </Row>
      {(!item?.vehicle?.vehicleFeaturesCustom ||
        item?.vehicle?.vehicleFeaturesCustom.length === 0) &&
      (!item?.vehicle?.vehicleFeaturesDictionary ||
        item?.vehicle?.vehicleFeaturesDictionary.length === 0) ? (
        <Row className="summary__car-features_not_specified m-0 mt-0">
          <p className="summary__description-paragraph text-base">
            {CommonString.FEATURED_NOT_SPECIFIED}
          </p>
        </Row>
      ) : (
        <Row className="summary__car-features m-0 ">
          {item?.vehicle?.vehicleFeaturesDictionary &&
            item?.vehicle?.vehicleFeaturesDictionary.map((feature) => (
              <div className="summary__feature-pills" key={feature.id}>
                <span className="summary__feature-pills-name">{feature.value}</span>
              </div>
            ))}
          {item?.vehicle?.vehicleFeaturesCustom &&
            item?.vehicle?.vehicleFeaturesCustom.map((feature) => (
              <div className="summary__feature-pills" key={uuid()}>
                <span className="summary__feature-pills-name">{feature}</span>
              </div>
            ))}
        </Row>
      )}
      {!!item?.vehicle?.damages?.length && (
        <>
          <Row className="main-description-container__section-title m-0 mt-0" id="condition">
            <Col md={12} className="p-0  text-lg p-4">
              Condition
            </Col>
          </Row>
          <Row
            className="vehicle-details__condition-content mt-0 p-0 g-0"
            md={5}
            sm={3}
            xs={1}
          >
            <Row className="w-100 condition-content__header g-0">
              <span className="header__title p-4">Condition inspection – overview</span>
            </Row>
            <VehicleConditionMap
              context="details"
              damages={item?.vehicle?.damages?.map((item) => ({
                ...item,
                x: item.coordinateX,
                y: item.coordinateY,
                section: item.damageSection,
              }))}
              onMapClick={() => {}}
              className="details"
              onMarkerClick={(index) => {
                setCurrentDamageIndex(index);
                setCurrentImageIndex(0);
              }}
            />
            <Row className="w-100 condition-content__header ">
              <span className="header__title p-4">Condition inspection – list</span>
            </Row>
            <Row className="w-100 px-4 g-0">
              {item?.vehicle?.damages?.map((item, index) => (
                <div className="condition-content__damage">
                  <div className="condition-content__damage-order">{`${index + 1}.`}</div>
                  <div className="condition-content__damage-info">
                    <span className="damage-info__type">
                      {DamageTypesName?.[item?.damageType]}
                    </span>
                    <span className="damage-info__description">{item?.description}</span>
                  </div>
                  <button
                    type="button"
                    className="condition-content__damage-action"
                    onClick={() => {
                      setCurrentDamageIndex(index);
                      setCurrentImageIndex(0);
                    }}
                  >
                    <span className="damage-action__text">Show</span>
                    <img
                      src={magnifierIcon}
                      alt="Red magnifier"
                      className="damage-action__img"
                    />
                  </button>
                </div>
              ))}
            </Row>
            <SweetAlert
              showCancel={item?.vehicle?.damages?.length > 1}
              showConfirm={item?.vehicle?.damages?.length > 1}
              show={currentDamageIndex !== null}
              onConfirm={() => {
                setCurrentDamageIndex((prev) => prev - 1);
                setCurrentImageIndex(0);
              }}
              onCancel={() => {
                setCurrentDamageIndex((prev) => prev + 1);
                setCurrentImageIndex(0);
              }}
              closeOnClickOutside={false}
              closeOnEsc={false}
              /* eslint-disable-next-line no-unsafe-optional-chaining */
              cancelBtnCssClass={`common-button red-button ${currentDamageIndex === item?.vehicle?.damages?.length - 1 ? 'damage-alert__btn--hidden' : ''}`}
              cancelBtnText="Next"
              confirmBtnText="Previous"
              customIcon={<img src={closeIcon} alt="Close icon" />}
              confirmBtnCssClass={`common-button red-button ${currentDamageIndex === 0 ? 'damage-alert__btn--hidden' : ''}`}
              title=""
              customClass="damage-alert"
            >
              <div className="sweet-alert__header">
                <span className="sweet-alert-text">Condition Inspection Details</span>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentDamageIndex(null);
                    setCurrentImageIndex(null);
                  }}
                >
                  <img src={closeIcon} alt="Close icon" />
                </button>
              </div>
              <div className="sweet-alert__content">
                <div className="damage-alert__header">
                  <div className="header__counter">{currentDamageIndex + 1}</div>
                  <div className="header__content">
                    <span className="content__damage-type">
                      {
                        DamageTypesName?.[
                          item?.vehicle?.damages?.[currentDamageIndex]?.damageType
                        ]
                      }
                    </span>
                    <span className="content__damage-description">
                      {item?.vehicle?.damages?.[currentDamageIndex]?.description}
                    </span>
                  </div>
                </div>

                <div className="slider-container">
                  {currentImageIndex !== 0 && (
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="slider-container__button slider-container__button--back"
                    >
                      <img
                        className="slider-container__button-icon "
                        src={arrowIcon}
                        alt="Arrow back icon"
                      />
                    </button>
                  )}

                  <div className="image-wrapper">
                    {(() => {
                      let src;
                      const damage = item?.vehicle?.damages?.[currentDamageIndex];
                      const photo = damage?.photos?.[currentImageIndex];
                      if (photo) {
                        src = photo.fileUrl;
                      }
                      return <img src={src} alt="slider" className="slider-image" />;
                    })()}
                    <div className="auction-list-item__image-pill image-wrapper__pill">
                      <img src={cameraIcon} alt="Social Media Icon" />
                      <span className="image-pill__counter">
                        {currentImageIndex + 1}/
                        {item?.vehicle?.damages?.[currentDamageIndex]?.photos?.length}
                      </span>
                    </div>
                  </div>

                  {currentImageIndex !==
                    // eslint-disable-next-line no-unsafe-optional-chaining
                    item?.vehicle?.damages?.[currentDamageIndex]?.photos?.length - 1 && (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="slider-container__button slider-container__button--next"
                    >
                      <img
                        className="slider-container__button-icon"
                        src={arrowIcon}
                        alt="Arrow next icon"
                      />
                    </button>
                  )}
                </div>
              </div>
            </SweetAlert>
          </Row>
        </>
      )}
      <Row
        className="main-description-container__section-title m-0 mt-0 p-4"
        id="service-history"
      >
        <Col md={12} className="p-0 text-lg">
          {item?.vehicle?.serviceHistory?.type === VehicleServiceHistory.NONE
            ? 'Service History'
            : VehicleServiceHistoryName[item?.vehicle?.serviceHistory?.type] || ''}
        </Col>
      </Row>
      {!item?.vehicle?.serviceHistory?.manualRecords?.length &&
      serviceHistoryThumbnailUrls?.length ? (
        ''
      ) : (
        <Row className="m-0 vehicle-details-service-history-content p-4">
          <Col md={12} className="p-0">
            {!item?.vehicle?.serviceHistory?.manualRecords?.length ? (
              <p className="summary__description-paragraph  text-base">
                {CommonString.HISTORY_NOT_SPECIFIED}
              </p>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {item?.vehicle?.serviceHistory?.manualRecords.map((item) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Row className="summary__service-list-item text-base">
                    <Col sm={12} md={6} className="service-list-item__date p-0 pe-md-3">
                      <p className="service-list-item__key">Service Date</p>
                      <p className="service-list-item__value">{dateToString(item.date)}</p>
                    </Col>
                    <Col sm={12} md={6} className="service-list-item__name p-0 pe-md-3">
                      <p className="service-list-item__key">Service Name</p>
                      <p className="service-list-item__value">{item.serviceName}</p>
                    </Col>
                    <Col sm={12} md={6} className="service-list-item__mileage p-0 ps-md-3">
                      <p className="service-list-item__key">Miles Service Performed</p>
                      <p className="service-list-item__value">
                        {item.mileage} mile{item.mileage > 1 ? 's' : ''}
                      </p>
                    </Col>
                    <Col sm={12} md={6} className="service-list-item__type p-0 ps-md-3">
                      <p className="service-list-item__key">Service Type</p>
                      <p className="service-list-item__value">
                        {ServiceTypeName?.[item.serviceType] || 'Not specified'}
                      </p>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Col>
        </Row>
      )}
      <div
        className={`m-0 summary__service-history p-4 ${!serviceHistoryThumbnailUrls?.length ? 'hide' : ''}`}
      >
        <span className="summary__service-history__title">Service Book Scans</span>
        <div>
          <div className="summary__service-history-list">
            {serviceHistoryThumbnailUrls.map((item, index) => (
              <div className="summary__service-history-list-item">
                <button
                  type="button"
                  className="summary__service-history-list-item-photo"
                  onClick={() => {
                    setCurrentImageIndex(index);
                  }}
                >
                  <img
                    className="summary__service-history-list-item-photo"
                    src={item}
                    alt="Vehicle"
                  />
                  <div className="summary__service-history-list-item-icon-wrapper">
                    <img
                      src={magnifierIcon}
                      alt="Magnifier icon"
                      className="summary__service-history-list-item-icon"
                    />
                  </div>
                </button>
              </div>
            ))}
          </div>
          <SweetAlert
            showCancel
            show={currentDamageIndex === null && currentImageIndex !== null}
            onConfirm={() => {
              setCurrentDamageIndex((prev) => prev - 1);
              setCurrentImageIndex(0);
            }}
            onCancel={() => {
              setCurrentDamageIndex((prev) => prev + 1);
              setCurrentImageIndex(0);
            }}
            closeOnClickOutside={false}
            closeOnEsc={false}
            cancelBtnCssClass="damage-alert__btn--hidden"
            cancelBtnText="Next"
            confirmBtnText="Previous"
            customIcon={<img src={closeIcon} alt="Close icon" />}
            confirmBtnCssClass="damage-alert__btn--hidden"
            title=""
            customClass="service-history-alert"
            showCloseButton={false}
            showConfirm={false}
          >
            <div className="sweet-alert__header">
              <span className="sweet-alert-text">Service Book Scans</span>
              <button
                type="button"
                onClick={() => {
                  setCurrentImageIndex(null);
                }}
              >
                <img src={closeIcon} alt="Close icon" />
              </button>
            </div>
            <div className="sweet-alert__content">
              <div className="slider-container">
                {currentImageIndex !== 0 && (
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="slider-container__button slider-container__button--back"
                  >
                    <img
                      className="slider-container__button-icon "
                      src={arrowIcon}
                      alt="Arrow back icon"
                    />
                  </button>
                )}

                <div className="image-wrapper">
                  {(() => {
                    let src;
                    const record =
                      item?.vehicle?.serviceHistory?.photoRecords?.[currentImageIndex];
                    if (record) {
                      src = record.fileUrl;
                    }
                    return <img src={src} alt="slider" className="slider-image" />;
                  })()}
                  <div className="auction-list-item__image-pill image-wrapper__pill">
                    <img src={cameraIcon} alt="Social Media Icon" />
                    <span className="image-pill__counter">
                      {currentImageIndex + 1}/
                      {item?.vehicle?.serviceHistory?.photoRecords?.length}
                    </span>
                  </div>
                </div>

                {currentImageIndex !==
                  // eslint-disable-next-line no-unsafe-optional-chaining
                  item?.vehicle?.serviceHistory?.photoRecords?.length - 1 && (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="slider-container__button slider-container__button--next"
                  >
                    <img
                      className="slider-container__button-icon"
                      src={arrowIcon}
                      alt="Arrow next icon"
                    />
                  </button>
                )}
              </div>
            </div>
          </SweetAlert>
        </div>
      </div>
      <Row className="main-description-container__section-title m-0 p-4">
        <Col md={12} className="p-0 pb-2 text-lg">
          Tyre Tread Depths
        </Col>
      </Row>
      <Row className="summary__services-and-tyres-wrapper text-base m-0 mt-0 p-4">
        <Col sm={12} md={6} className="summary__services-and-tyres-item pe-md-3">
          <p className="m-0">Nearside Front</p>
          <p className="m-0 fw-600">
            {item?.vehicle?.tireNearsideFrontMm
              ? `${item?.vehicle?.tireNearsideFrontMm}mm`
              : '-'}
          </p>
        </Col>
        <Col sm={12} md={6} className="summary__services-and-tyres-item ps-md-3">
          <p className="m-0">Offside Front</p>
          <p className="m-0 fw-600">
            {item?.vehicle?.tireOffsideFrontMm
              ? `${item?.vehicle?.tireOffsideFrontMm}mm`
              : '-'}
          </p>
        </Col>
        <Col sm={12} md={6} className="summary__services-and-tyres-item pe-md-3">
          <p className="m-0">Nearside Rear</p>
          <p className="m-0 fw-600">
            {item?.vehicle?.tireNearsideRearMm
              ? `${item?.vehicle?.tireNearsideRearMm}mm`
              : '-'}
          </p>
        </Col>
        <Col sm={12} md={6} className="summary__services-and-tyres-item ps-md-3">
          <p className="m-0">Offside Rear</p>
          <p className="m-0 fw-600">
            {item?.vehicle?.tireOffsideRearMm ? `${item?.vehicle?.tireOffsideRearMm}mm` : '-'}
          </p>
        </Col>
      </Row>
      {/*<Row*/}
      {/*  className="main-description-container__section-title m-0 mt-4"*/}
      {/*  id="vehicle-details-description"*/}
      {/*>*/}
      {/*  <Col md={12} className="p-0 pb-2 text-lg">*/}
      {/*    Disclaimer*/}
      {/*  </Col>*/}
      {/*</Row>*/}
      {/*<Row className="summary__description mt-3 text-base">*/}
      {/*  <Col md={12}>*/}
      {/*    Whilst we endeavour to present and describe all vehicles accurately. Please allow an*/}
      {/*    additional £150 for anything that may have been missed.*/}
      {/*  </Col>*/}
      {/*</Row>*/}
    </div>
  );

  const hasDamages =
    Array.isArray(item?.vehicle?.damages) && Boolean(item?.vehicle?.damages.length);

  const sectionTabs = [
    {
      name: 'Gallery',
      id: 'gallery',
    },
    {
      name: 'Overview',
      id: 'overview',
    },
    {
      name: 'Equipment',
      id: 'equipment',
    },
    ...(hasDamages
      ? [
          {
            name: 'Condition',
            id: 'condition',
          },
        ]
      : []),
    {
      name: 'Service History',
      id: 'service-history',
    },
  ];
  const [isFavouriteAuction, setIsFavouriteAuction] = useState(params?.isFavourite);
  const memberId = useSelector((state) => state.auth.get('id'));
  const favouriteLocationId = useDefaultBidLocationId();
  const isAllLocationsSelected = useIsAllLocationsSelected();
  const didUserToggleRef = useRef(false);
  const fetchedFavouriteLocationRef = useRef(null);

  useEffect(() => {
    if (!memberId || !item?.id || !favouriteLocationId) return;
    const isLocationChange = fetchedFavouriteLocationRef.current !== favouriteLocationId;
    fetchedFavouriteLocationRef.current = favouriteLocationId;
    if (isLocationChange) {
      didUserToggleRef.current = false;
    }
    const body = {
      criteria: { organizationLocationId: favouriteLocationId },
      page: { pageNumber: 0, pageSize: 100 },
    };
    (async () => {
      try {
        const res = await dispatch(getFavouriteAuctions(body));
        const content = res?.data?.data?.content || [];
        const found =
          Array.isArray(content) &&
          content.some((it) => {
            const favId = it?.id ?? it?.auctionId ?? it?.auction?.id;
            return String(favId) === String(item.id);
          });
        if (!didUserToggleRef.current) setIsFavouriteAuction(found);
      } catch (e) {
        // ignore
      }
    })();
  }, [dispatch, memberId, item?.id, favouriteLocationId]);

  const _handleToggleFavourite = async () => {
    didUserToggleRef.current = true;
    const wasFavourite = !!isFavouriteAuction;
    // optimistic toggle
    setIsFavouriteAuction((prevState) => !prevState);
    const body = { memberId, auctionId: item.id, organizationLocationId: favouriteLocationId };
    try {
      let res;
      if (wasFavourite) {
        res = await dispatch(
          deleteFavouriteAuction(
            isAllLocationsSelected ? { ...body, organizationLocationId: null } : body,
          ),
        );
      } else {
        res = await dispatch(
          addFavouriteAuction(
            isAllLocationsSelected ? { ...body, organizationLocationId: null } : body,
          ),
        );
      }
      if (!res || res.status !== 200) {
        // revert on failure
        setIsFavouriteAuction((prev) => !prev);
      }
    } catch (e) {
      setIsFavouriteAuction((prev) => !prev);
    }
  };
  const isPublished = item?.status === AuctionStatus.PUBLISHED;
  const renderActionButtons = () => {
    const shareUrl = `${window.location.origin}/auction/${item?.id}`;
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {isPublished ? (
          <>
            <button
              type="button"
              className="auction-list-item__action-btn"
              id={`auctionDetailsItem-${item?.id}`}
            >
              <img src={shareIcon} alt="Share icon" />
            </button>
            <ShareAuctionBox
              target={`auctionDetailsItem-${item?.id}`}
              placement="bottom"
              shareUrl={shareUrl}
            />
            {(listType === auctionListType.MY_BIDS ||
              (listType === auctionListType.SEARCH &&
                item?.seller?.organizationContact?.id !== organizationId)) && (
              <button
                type="button"
                className={`auction-list-item__action-btn ${isFavouriteAuction ? 'auction-list-item__action-btn--red' : ''}`}
                onClick={_handleToggleFavourite}
              >
                {!isFavouriteAuction ? (
                  <img src={favoriteIcon} alt="Black heart icon" />
                ) : (
                  <img src={favoriteRedIcon} alt="Red heart icon" />
                )}
              </button>
            )}
          </>
        ) : null}
      </>
    );
  };

  const renderHighlightBadges = () =>
    !!item?.vehicle?.vehicleHighlightedInfo?.length && (
      <div className="badge">
        <span>{item?.vehicle?.vehicleHighlightedInfo}</span>
      </div>
    );

  const render = () => (
    <div className="auction-details-wrapper-outer">
      <div className="auction-details-wrapper account-layout__content-column">
        <Row>
          <Col xxl={8}>
            <div className="auction-details__tabs-wrapper">
              <div className="auction-details__tabs d-none d-md-flex">
                {sectionTabs.map((item) => (
                  <button
                    className="auction-details__tab"
                    type="button"
                    onClick={(e) => onNavItemClick(e, item.id)}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
            <Row className="m-0 image-slider-wrapper" id="gallery">
              {photos.length > 0 || videos.length > 0 ? (
                <Col md={12} className="p-0">
                  <ImagesSlider
                    images={photos}
                    videos={videos}
                    actionButtons={renderActionButtons}
                  />
                </Col>
              ) : (
                ''
              )}
            </Row>
            {isDesktop && renderSummary()}
          </Col>
          <Col xxl={4}>
            <div className={isDesktop ? 'content-column__make-offer' : ''}>
              {!isMobile && (
                <Row className="p-4 pb-2">
                  <Col>{renderHighlightBadges()}</Col>
                  <Col className="action-buttons--not-mobile">{renderActionButtons()}</Col>
                </Row>
              )}
              <Row>
                <Col xs={12} md={6} xxl={12}>
                  <Row className="px-4" md={12}>
                    <Col className="p-0">
                      <h4 className="font-weight-bold text-2xl mt-3">
                        {`${item?.vehicle?.vehicleModel?.makeName} ${item?.vehicle?.vehicleModel?.name}`}
                        {/*{prepareVehicleTitle(item?.vehicle) || CommonString.VEHICLE_NOT_SPECIFIED}*/}
                      </h4>
                      <span>{item?.vehicle?.derivative}</span>
                      <Row className="registration-wrapper mt-2 mb-4 gap-md-3 gap-xxl-0">
                        <Col xs={6} md={12} xxl={6}>
                          <div className="registration-plate">
                            {item?.vehicle?.registrationPlate}
                          </div>
                        </Col>
                        <Col xs={6} md={12} xxl={6}>
                          <div className="location">
                            <img src={locationIcon} alt="Social Media Icon" />
                            <span className="location-address">{`${item?.location?.city}, ${item?.location?.county}`}</span>
                          </div>
                        </Col>
                        <div className="container mt-4">
                          <div
                            className={classNames(
                              'row row-cols-2 gy-1 row-cols-md-1 row-cols-xxl-2',
                              isMobile ? 'gx-1' : 'gx-3',
                            )}
                          >
                            <div className="main-info-item col">
                              <div className={isDesktop && 'p-2'}>
                                Year
                                <span className="fw-bold">{item?.vehicle?.year}</span>
                              </div>
                            </div>
                            <div className="main-info-item col">
                              <div className={isDesktop && 'p-2'}>
                                Fuel
                                <span className="fw-bold">{item?.vehicle?.fuel}</span>
                              </div>
                            </div>
                            <div className="main-info-item col">
                              <div className={isDesktop && 'p-2'}>
                                Mileage
                                <span className="fw-bold">{item?.vehicle?.mileage} miles</span>
                              </div>
                            </div>
                            <div className="main-info-item col">
                              <div className={isDesktop && 'p-2'}>
                                Doors
                                <span className="fw-bold">{item?.vehicle?.doors}</span>
                              </div>
                            </div>
                            <div className="main-info-item col">
                              <div className={isDesktop && 'p-2'}>
                                Transmission
                                <span className="fw-bold">{item?.vehicle?.transmission}</span>
                              </div>
                            </div>
                            <div className="main-info-item col">
                              <div className={isDesktop && 'p-2'}>
                                Previous Keepers
                                <span className="fw-bold">
                                  {item?.vehicle?.previousKeepersInTotal}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Row>
                      {isMobile && renderHighlightBadges()}
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} md={6} xxl={12} className="mb-4 mt-4 mt-lg-0">
                  <Row className="px-2">
                    <AuctionMainDescriptionContainer
                      item={item}
                      windowFocus={windowFocus}
                      organizationId={organizationId}
                      listType={listType}
                      isDetailsPage={true}
                      pattern={[
                        SlotItems.CURRENT_BID,
                        SlotItems.TIME_LEFT,
                        SlotItems.AUCTION_END,
                        SlotItems.AUCTION_STATUS_BOX,
                      ]}
                      bidBarMessage={bidBarMessage}
                      bidBarColor={bidBarColor}
                      auctionTimestamp={auctionTimestamp}
                      highestBid={highestBid}
                      proxyBid={proxyBid}
                      shouldPriceBeGreen={shouldPriceBeGreen}
                      context={context}
                    />
                  </Row>
                  {isMobile || isDesktop ? renderDetailsSection() : null}
                </Col>
              </Row>
              {!isMobile && !isDesktop && renderDetailsSection()}
            </div>
          </Col>
        </Row>
        {!isDesktop && renderSummary()}
      </div>
    </div>
  );

  const isWonBidder = winningBid?.bidderId === currentMemberId;

  const isLostBidder =
    (item?.type === AuctionStatusType.TRYING_TO_DEAL || item?.status === AuctionStatus.SOLD) &&
    item?.status !== AuctionStatus.PUBLISHED &&
    item?.seller?.id !== currentMemberId &&
    item?.bids.length > 0 &&
    item?.bids?.filter((it) => it.isHighest).length === 0;

  const isFinishedAuction =
    item?.type === AuctionStatusType.TRYING_TO_DEAL &&
    item?.status !== AuctionStatus.PUBLISHED &&
    item?.status !== AuctionStatus.SOLD &&
    item?.seller?.id !== currentMemberId &&
    item?.bids.length === 0;

  const renderBoxVariant = () => {
    if (isFinishedAuction) {
      return BoxStatusVariants.SECONDARY;
    }
    if (
      isLostBidder ||
      ((item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.REJECTED) &&
        item?.bids?.filter((it) => it.isHighest).length === 0)
    ) {
      return BoxStatusVariants.DANGER;
    }
    if (item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.WAITING) {
      return (isWonBidder && item?.isBuyerAcceptanceAllowed) ||
        (item?.seller?.id === currentMemberId && item?.isSellerAcceptanceAllowed)
        ? BoxStatusVariants.SUCCESS
        : BoxStatusVariants.WARNING;
    }
    return null;
  };

  const renderStatusBoxContent = () => {
    if (isFinishedAuction) {
      return AuctionStatusName.ENDED;
    }
    if (
      isLostBidder ||
      ((item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.REJECTED) &&
        item?.bids?.filter((it) => it.isHighest).length === 0)
    ) {
      return BidStatus.LOST;
    }
    if (
      isWonBidder &&
      (item?.status === AuctionStatus.TO_ACCEPT || item?.status === AuctionStatus.WAITING)
    ) {
      return item?.isBuyerAcceptanceAllowed
        ? AuctionStatusName.TO_ACCEPT
        : AuctionStatusName.WAITING;
    }
    return null;
  };

  return false ? (
    <AccountLayoutWrapper
      {...wrapperProps}
      navTitle={
        wrapperProps?.navTitle || (
          <AccountLayoutTitle>
            <AuctionStatusBox
              auction={item}
              content={renderStatusBoxContent()}
              customBoxVariant={renderBoxVariant()}
            />
          </AccountLayoutTitle>
        )
      }
      navContent={
        listType === auctionListType.MY_BIDS || listType === auctionListType.SEARCH ? (
          <PlaceBidBox
            proxyBidInPence={proxyBid || pencesToPounds(item?.proxyBidInPence)}
            highestBidInPence={highestBid || pencesToPounds(item?.highestBidInPence) || 0}
            shouldPriceBeGreen={shouldPriceBeGreen}
            item={item}
            refreshItem={refreshItem}
            bids={bidsNotification}
            changeStatus={changeStatus}
            status={status}
          />
        ) : (
          ''
        )
      }
    >
      {render()}
    </AccountLayoutWrapper>
  ) : (
    <>{render()}</>
  );
}

AuctionDetailsItem.defaultProps = {
  nextStep: () => {},
  inWrapper: false,
  setIsRepublish: () => {},
  wrapperProps: null,
  navContentRef: null,
  auctionTimestamp: null,
  context: null,
};

AuctionDetailsItem.propTypes = {
  item: PropTypes.object.isRequired,
  listType: PropTypes.string.isRequired,
  refreshItem: PropTypes.func,
  typeSseClient: PropTypes.string.isRequired,
  nextStep: PropTypes.func,
  setIsRepublish: PropTypes.func,
  navContentRef: PropTypes.object,
  inWrapper: PropTypes.bool,
  wrapperProps: PropTypes.object,
  auctionTimestamp: PropTypes.object,
  context: PropTypes.oneOf([
    AuctionListTemplateContextType.SEARCH,
    AuctionListTemplateContextType.BUYING,
    AuctionListTemplateContextType.SELLING,
    AuctionListTemplateContextType.FAVOURITE_OFFERS,
  ]),
};
AuctionDetailsItem.defaultProps = {
  refreshItem: () => {},
};

export default AuctionDetailsItem;
